import { parseISO } from "date-fns";

import { DELIVERY_LOCATIONS } from "../../../../../constants";
import { formatNumber } from "../../../../../utils";
import { AddressHolder, CustomExchangeLocation, NamedEntity } from "../../../types";
import { CustomDeliveryLocationDetails } from "../../OfferCard/interfaces";
import type { CounterOfferFormValues } from "../../CounterOfferModal/schema";
import type { DeliveryLocationValue } from "../../../../DirectOfferDetails/schema";

export interface NegotiationProposal {
  user: {
    id: number;
    first_name: string;
    last_name: string;
    roles: string[];
  };

  uuid: string;
  // Unique identifier for the object
  mobile_creation_time?: string;
  // When the object was created on the mobile
  mobile_last_change_time?: string;
  // When the object was last changed on the mobile

  minimum_quantity: number;
  maximum_quantity: number;

  price: number;
  delivery_cost?: string | number | null;
  // Only on a demand line's delivery round: its payment also covered the demand's delivery cost.
  includes_delivery_cost?: boolean;
  planned_exchange_time?: string;
  // The time that the exchange for this offer is planned to take place
  delivery_location: string;
  text: string;
  responded: boolean;
  // Has the target user responded to this proposal?
  trader_market_offer: number;
  // Which offer is this proposal for?
  backend_creation_time: string;
  frontend_creation_time: string;
  proposer: "aggregator" | "trader";
  proposal_type?: string;
  custom_delivery_location_details?: CustomDeliveryLocationDetails;
  custom_delivery_location?: number;
  custom_delivery_location_type?: number;
}

export const makeProposal = (
  proposal: Partial<NegotiationProposal>,
  trader: AddressHolder | undefined,
  aggregator: AddressHolder | undefined,
  t: (key: string) => string,
  customExchangeLocations: CustomExchangeLocation[],
  exchangeLocationTypes: NamedEntity[],
  administrativeAreas: NamedEntity[]
): CounterOfferFormValues => {
  let addressHolder: string = "";
  let value: DeliveryLocationValue = "";

  switch (proposal.delivery_location) {
    case "drop_at_trader": {
      const traderDeliveryLocationType = DELIVERY_LOCATIONS.find(d => d.value === "drop_at_trader");
      value = "drop_at_trader";
      const traderPhysicalAddress = trader?.physical_address;
      addressHolder = traderDeliveryLocationType
        ? `${t(traderDeliveryLocationType.translationKey)}: `
        : "";
      addressHolder = traderPhysicalAddress ? addressHolder + traderPhysicalAddress : "";
      break;
    }
    case "pick_from_aggregator": {
      const aggregatorDeliveryLocationType = DELIVERY_LOCATIONS.find(
        d => d.value === "pick_from_aggregator"
      );
      value = "pick_from_aggregator";
      const aggregatorPhysicalAddress = aggregator?.physical_address;
      addressHolder = aggregatorDeliveryLocationType
        ? `${t(aggregatorDeliveryLocationType.translationKey)}: `
        : "";
      addressHolder = aggregatorPhysicalAddress ? addressHolder + aggregatorPhysicalAddress : "";
      break;
    }
    case "custom_exchange_location": {
      const customExchangeLocation = customExchangeLocations.find(
        loc => loc.id === proposal.custom_delivery_location
      );

      const customExchangeLocationType = exchangeLocationTypes.find(
        exchangeLocationType => exchangeLocationType.id === proposal.custom_delivery_location_type
      );

      const location = customExchangeLocation
        ? administrativeAreas?.find(area => area.id === customExchangeLocation.location)
        : undefined;

      value = {
        deliveryLocationType: "custom_exchange_location",
        custom_delivery_location:
          customExchangeLocation?.id ?? proposal.custom_delivery_location ?? 0,
        custom_delivery_location_type: proposal.custom_delivery_location_type ?? 0,
      };
      const name = customExchangeLocation?.name;
      const administrativeLocation = location?.name;
      const physical_address = customExchangeLocation?.physical_address;
      const exchange_location_name = customExchangeLocationType?.name;
      addressHolder = [name, physical_address, exchange_location_name, administrativeLocation]
        .filter(Boolean)
        .join(", ");
      break;
    }
    default:
      addressHolder = "";
      break;
  }
  const label = addressHolder && addressHolder !== "" ? addressHolder : "Name unavailable";

  return {
    price: proposal.price ? formatNumber(proposal.price, false) : "",
    minimum_quantity: proposal.minimum_quantity
      ? formatNumber(proposal.minimum_quantity, false)
      : "",
    maximum_quantity: proposal.maximum_quantity
      ? formatNumber(proposal.maximum_quantity, false)
      : "",
    delivery_cost:
      proposal.delivery_cost !== null && proposal.delivery_cost !== undefined
        ? formatNumber(proposal.delivery_cost, false)
        : "",
    planned_exchange_time: proposal.planned_exchange_time
      ? parseISO(proposal.planned_exchange_time)
      : new Date(),
    delivery_location: {
      label,
      value,
    },
    text: "",
  };
};
